// SvgTemplateEditor.jsx
// A lightweight, dependency-free SVG template editor:
//   • live preview (renders the SVG string)
//   • drag to move any text/image element
//   • click to select, double-click a text to edit its words
//   • toolbar: add text, delete selected, bigger/smaller font
//   • an editable SVG-code box, kept in two-way sync with the preview
// Used by the certificate page and the student ID-card tab.
//
// Controlled component: parent owns the SVG string via `value` / `onChange`.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { getViewBox } from "./svgUtils";

const SVG_NS = "http://www.w3.org/2000/svg";
const DRAGGABLE_TAGS = ["text", "image", "g"];

const resolveDraggable = (target, svg) => {
  let el = target;
  while (el && el !== svg) {
    const tag = (el.tagName || "").toLowerCase();
    if (DRAGGABLE_TAGS.includes(tag) || el.hasAttribute?.("data-draggable")) {
      // climb a tspan up to its <text>
      if (tag === "tspan" && el.parentNode) return el.parentNode;
      return el;
    }
    el = el.parentNode;
  }
  return null;
};

const resolveText = (target, svg) => {
  let el = target;
  while (el && el !== svg) {
    const tag = (el.tagName || "").toLowerCase();
    if (tag === "text") return el;
    if (tag === "tspan" && el.parentNode) return el.parentNode;
    el = el.parentNode;
  }
  return null;
};

const getTranslate = (el) => {
  const t = el.getAttribute("transform") || "";
  const m = t.match(/translate\(\s*([\-\d.]+)[ ,]+([\-\d.]+)\s*\)/);
  return m ? { x: parseFloat(m[1]), y: parseFloat(m[2]) } : { x: 0, y: 0 };
};
const setTranslate = (el, x, y) => {
  el.setAttribute("transform", `translate(${x.toFixed(2)},${y.toFixed(2)})`);
};

const SvgTemplateEditor = ({ value, onChange, width, height, hint, previewMaxWidth }) => {
  const containerRef = useRef(null);
  const lastEmitted = useRef(null);
  const dragRef = useRef(null);
  const [selectedEl, setSelectedEl] = useState(null);
  const [overlay, setOverlay] = useState(null); // {left,top,width,height}
  const [editing, setEditing] = useState(null); // {left,top,width,height,text}

  const vb = getViewBox(value);
  const vbW = width || vb.width;
  const vbH = height || vb.height;

  const emit = useCallback(
    (svgString) => {
      lastEmitted.current = svgString;
      onChange && onChange(svgString);
    },
    [onChange]
  );

  // Render the SVG string into the preview (only when it changed externally).
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    if (value === lastEmitted.current) return; // we already have this DOM
    c.innerHTML = value || "";
    const svg = c.querySelector("svg");
    if (svg) {
      svg.style.width = "100%";
      svg.style.height = "auto";
      svg.style.display = "block";
      svg.style.touchAction = "none";
      svg.style.userSelect = "none";
    }
    lastEmitted.current = value;
    setSelectedEl(null);
    setOverlay(null);
  }, [value]);

  const positionFor = (el) => {
    const c = containerRef.current;
    if (!c || !el || !el.getBoundingClientRect) return null;
    const cr = c.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    return {
      left: r.left - cr.left,
      top: r.top - cr.top,
      width: r.width,
      height: r.height,
    };
  };

  const selectElement = (el) => {
    setSelectedEl(el);
    setOverlay(positionFor(el));
  };

  const scaleFactors = (svg) => {
    const rect = svg.getBoundingClientRect();
    const baseW = svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.width;
    const baseH = svg.viewBox && svg.viewBox.baseVal && svg.viewBox.baseVal.height;
    return {
      sx: baseW && rect.width ? baseW / rect.width : 1,
      sy: baseH && rect.height ? baseH / rect.height : 1,
    };
  };

  const onPointerDown = (e) => {
    const c = containerRef.current;
    const svg = c && c.querySelector("svg");
    if (!svg) return;
    const el = resolveDraggable(e.target, svg);
    if (!el) {
      setSelectedEl(null);
      setOverlay(null);
      return;
    }
    selectElement(el);
    const { sx, sy } = scaleFactors(svg);
    const base = getTranslate(el);
    dragRef.current = {
      el,
      startX: e.clientX,
      startY: e.clientY,
      baseX: base.x,
      baseY: base.y,
      sx,
      sy,
      moved: false,
    };
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    e.preventDefault();
  };

  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = (e.clientX - d.startX) * d.sx;
    const dy = (e.clientY - d.startY) * d.sy;
    setTranslate(d.el, d.baseX + dx, d.baseY + dy);
    d.moved = true;
    setOverlay(positionFor(d.el));
  };

  const onPointerUp = () => {
    const d = dragRef.current;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    if (d && d.moved) {
      const svg = containerRef.current.querySelector("svg");
      if (svg) emit(svg.outerHTML);
    }
    dragRef.current = null;
  };

  const onDoubleClick = (e) => {
    const c = containerRef.current;
    const svg = c && c.querySelector("svg");
    if (!svg) return;
    const text = resolveText(e.target, svg);
    if (!text) return;
    selectElement(text);
    const pos = positionFor(text);
    setEditing({ ...pos, text: text.textContent, el: text });
  };

  const commitEdit = () => {
    if (editing && editing.el) {
      editing.el.textContent = editing.text;
      const svg = containerRef.current.querySelector("svg");
      if (svg) emit(svg.outerHTML);
      setOverlay(positionFor(editing.el));
    }
    setEditing(null);
  };

  const addText = () => {
    const svg = containerRef.current && containerRef.current.querySelector("svg");
    if (!svg) return;
    const t = document.createElementNS(SVG_NS, "text");
    t.setAttribute("x", (vbW / 2).toFixed(0));
    t.setAttribute("y", (vbH / 2).toFixed(0));
    t.setAttribute("text-anchor", "middle");
    t.setAttribute("font-size", Math.round(vbH / 22));
    t.setAttribute("fill", "#111111");
    t.textContent = "New text";
    svg.appendChild(t);
    emit(svg.outerHTML);
    selectElement(t);
  };

  const deleteSelected = () => {
    if (!selectedEl) return;
    const svg = containerRef.current && containerRef.current.querySelector("svg");
    selectedEl.remove();
    setSelectedEl(null);
    setOverlay(null);
    if (svg) emit(svg.outerHTML);
  };

  const changeFontSize = (delta) => {
    if (!selectedEl) return;
    const tag = (selectedEl.tagName || "").toLowerCase();
    if (tag !== "text" && tag !== "tspan") return;
    const cur =
      parseFloat(selectedEl.getAttribute("font-size")) ||
      parseFloat(window.getComputedStyle(selectedEl).fontSize) ||
      16;
    const next = Math.max(4, cur + delta);
    selectedEl.setAttribute("font-size", next);
    const svg = containerRef.current.querySelector("svg");
    if (svg) {
      emit(svg.outerHTML);
      setOverlay(positionFor(selectedEl));
    }
  };

  const btn = {
    fontSize: 13,
    padding: "4px 10px",
    marginRight: 6,
    border: "1px solid #ccc",
    borderRadius: 6,
    background: "#fff",
    cursor: "pointer",
  };

  return (
    <div>
      {/* toolbar */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        <button type="button" style={btn} onClick={addText}>➕ Add text</button>
        <button type="button" style={btn} onClick={() => changeFontSize(2)} disabled={!selectedEl}>A+</button>
        <button type="button" style={btn} onClick={() => changeFontSize(-2)} disabled={!selectedEl}>A−</button>
        <button type="button" style={{ ...btn, color: "#b00020" }} onClick={deleteSelected} disabled={!selectedEl}>🗑 Delete</button>
        <span style={{ fontSize: 12, color: "#888", marginLeft: 6 }}>
          {hint || "Drag to move • click to select • double-click text to edit"}
        </span>
      </div>

      {/* preview + overlay */}
      <div style={{ position: "relative", border: "1px solid #e3e3e3", borderRadius: 8, background: "#fafafa", overflow: "hidden", maxWidth: previewMaxWidth || "100%", marginLeft: previewMaxWidth ? "auto" : undefined, marginRight: previewMaxWidth ? "auto" : undefined }}>
        <div
          ref={containerRef}
          onPointerDown={onPointerDown}
          onDoubleClick={onDoubleClick}
          style={{ width: "100%", cursor: "move" }}
        />
        {overlay && !editing && (
          <div
            style={{
              position: "absolute",
              left: overlay.left - 3,
              top: overlay.top - 3,
              width: overlay.width + 6,
              height: overlay.height + 6,
              border: "1.5px dashed #2962ff",
              borderRadius: 3,
              pointerEvents: "none",
              boxShadow: "0 0 0 9999px rgba(0,0,0,0)",
            }}
          />
        )}
        {editing && (
          <input
            autoFocus
            value={editing.text}
            onChange={(e) => setEditing({ ...editing, text: e.target.value })}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") setEditing(null);
            }}
            style={{
              position: "absolute",
              left: Math.max(0, editing.left - 2),
              top: Math.max(0, editing.top - 2),
              minWidth: 120,
              fontSize: 14,
              padding: "2px 6px",
              border: "1.5px solid #2962ff",
              borderRadius: 4,
              zIndex: 5,
            }}
          />
        )}
      </div>

      {/* SVG code box */}
      <div style={{ marginTop: 10 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: "#555" }}>SVG code (editable)</label>
        <textarea
          value={value || ""}
          // Do NOT use emit() here: editing the code must re-render the
          // preview, so we let `value` differ from lastEmitted and the
          // sync effect re-injects the DOM.
          onChange={(e) => onChange && onChange(e.target.value)}
          spellCheck={false}
          style={{
            width: "100%",
            height: 180,
            marginTop: 4,
            fontFamily: "monospace",
            fontSize: 12,
            border: "1px solid #ddd",
            borderRadius: 8,
            padding: 10,
            whiteSpace: "pre",
            overflow: "auto",
          }}
        />
      </div>
    </div>
  );
};

export default SvgTemplateEditor;
